import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { Check, FileText, GraduationCap, RefreshCw, ArrowUpRight, ExternalLink, Plus, Pencil } from 'lucide-react';
import { usePublicSettings, patchPublicSettings } from '../hooks/usePublicSettings';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText, useEditMode } from '../components/InlineEditable';
import EditableList from '../components/EditableList';
import PageHero from '../components/PageHero';
import { FAQ } from '../data/mock';
import { api } from '../lib/api';
import { toast } from 'sonner';

/**
 * Per-tab default content. Every editable string is a `settingKey` so admin
 * can rewrite it inline. Each tab keeps its OWN namespace of keys, so
 * "Üniversite" and "İlk-Orta-Lise" edits stay independent.
 */
const TAB_DEFS = {
  universite: {
    slug: 'universite',
    label: 'Üniversite',
    icon: GraduationCap,
    hero: {
      kicker: 'Üniversite Bursları',
      title: 'Üniversite bursu ile eğitimin yanında.',
      text: "İstanbul'daki devlet üniversitelerinde okuyan başarılı ancak maddi desteğe ihtiyaç duyan öğrencilerimize karşılıksız burs veriyoruz.",
    },
    cards: [
      { title: 'Kimler Başvurabilir', text: "İstanbul'da bir devlet üniversitesini kazanmış öğrenciler başvurabilir.", tag: 'Aktif' },
      { title: 'Değerlendirme', text: 'Başvurunuz akademik başarınız, aile durumunuz ve mülakat sonrası değerlendirilir.', tag: '' },
      { title: 'Ödeme Dönemi', text: 'Burslar 9 ay boyunca (Ekim-Haziran) her ayın başında hesabınıza yatırılır.', tag: '' },
    ],
    criteria: {
      kicker: 'Burs Koşulları',
      title: 'Kimler başvurabilir?',
      list: [
        "Devlet üniversitesinde örgün eğitim alan öğrenciler",
        "İstanbul'da bir devlet üniversitesini kazanmış öğrenciler",
        "Annesi veya babası hayatta olmayan / eğitimi devam eden kardeşleri olan öğrenciler",
        "Ekonomik yetersizliklerle eğitimini sürdürmekte zorlanan öğrenciler",
      ],
      note: 'Not: Özel ve vakıf üniversiteleri, ikinci öğretim, açık/uzaktan öğretim, yüksek lisans, doktora ve İstanbul dışındaki üniversiteler burs yönetmeliğimiz kapsamı dışındadır.',
    },
    cta: {
      kicker: 'Başvuru',
      title: 'Üniversite bursu için başvur',
      text: 'Bilgilerinizi tam ve doğru olarak doldurun. Değerlendirme sürecimiz ortalama 2-3 hafta sürer.',
      button: 'Burs Başvuru Sayfası',
      url: '/burs/basvuru',
    },
    docs: {
      kicker: 'Gerekli Evraklar',
      title: 'Başvuru için hazırlayın',
      list: ['Öğrenci Belgesi', 'Transkript', 'Gelir Belgesi', 'Vukuatlı Aile Kayıt', 'Nüfus Sureti', 'Askerlik Durum (Erkek)', 'Banka IBAN', 'Fotoğraf'],
    },
  },
  'ilk-orta-lise': {
    slug: 'ilk-orta-lise',
    label: 'İlk · Orta · Lise',
    icon: GraduationCap,
    hero: {
      kicker: 'İlk · Orta · Lise Bursları',
      title: 'Küçük yaşta eğitim, büyük gelecek.',
      text: 'İlkokul, ortaokul ve lise düzeyinde devlet okullarında öğrenim gören başarılı ancak maddi desteğe ihtiyaç duyan öğrencilerimize karşılıksız burs veriyoruz.',
    },
    cards: [
      { title: 'Kimler Başvurabilir', text: 'Devlet okullarında öğrenim gören ilkokul, ortaokul ve lise öğrencileri başvurabilir.', tag: 'Aktif' },
      { title: 'Değerlendirme', text: 'Başvurunuz okul karnesi, öğretmen referansı ve aile durumu değerlendirilerek belirlenir.', tag: '' },
      { title: 'Ödeme Dönemi', text: 'Burslar eğitim-öğretim yılı boyunca 9 ay periyodunda ödenir.', tag: '' },
    ],
    criteria: {
      kicker: 'Burs Koşulları',
      title: 'Kimler başvurabilir?',
      list: [
        'Devlet ilkokul, ortaokul veya lisesinde öğrenim gören öğrenciler',
        'Not ortalaması yüksek, disiplinsiz notu bulunmayan öğrenciler',
        'Annesi veya babası hayatta olmayan / eğitimi devam eden kardeşleri olan öğrenciler',
        'Ekonomik yetersizliklerle eğitimini sürdürmekte zorlanan aileler',
      ],
      note: 'Not: Özel okullar burs yönetmeliğimiz kapsamı dışındadır. Sadece devlet okullarında okuyan öğrencilere burs verilir.',
    },
    cta: {
      kicker: 'Başvuru',
      title: 'İlk · Orta · Lise bursu için başvur',
      text: 'Velisi tarafından doldurulan başvuruyu sunmanız yeterlidir. Değerlendirme sürecimiz ortalama 2-3 hafta sürer.',
      button: 'Burs Başvuru Sayfası',
      url: '/burs/basvuru',
    },
    docs: {
      kicker: 'Gerekli Evraklar',
      title: 'Başvuru için hazırlayın',
      list: ['Öğrenci Belgesi', 'Karne / Not Belgesi', 'Öğretmen Referansı', 'Gelir Belgesi', 'Vukuatlı Aile Kayıt', 'Nüfus Sureti', 'Banka IBAN', 'Fotoğraf'],
    },
  },
  yenileme: {
    slug: 'yenileme',
    label: 'Kayıt Yenileme',
    icon: RefreshCw,
    hero: {
      kicker: 'Kayıt Yenileme',
      title: 'Mevcut bursiyerler için kayıt yenileme.',
      text: 'Mevcut YEKÜV bursiyerlerimizin her eğitim-öğretim yılı başında kayıt yenileme işlemi yapması gereklidir.',
    },
    cards: [
      { title: 'Ne Zaman?', text: 'Kayıt yenileme dönemi her yıl Eylül ayında açılır, Ekim sonunda kapanır.', tag: 'Süreli' },
      { title: 'Gerekli Belgeler', text: 'Güncel öğrenci belgesi ve transkript ile başvurunuzu yenileyin.', tag: '' },
      { title: 'Onay Süreci', text: 'Yenileme onaylandığında burs ödemeleri Ekim ayından itibaren başlar.', tag: '' },
    ],
    criteria: {
      kicker: 'Yenileme Şartları',
      title: 'Kimler kayıt yenileyebilir?',
      list: [
        'YEKÜV bursu almış olan mevcut bursiyerler',
        'Öğrenime aynı okulda kesintisiz devam eden öğrenciler',
        'Not ortalaması yönetmelikte belirlenen alt limitin üzerinde olan öğrenciler',
        'Bursiyer sorumluluklarını yerine getiren öğrenciler',
      ],
      note: 'Not: Kayıt yenilemeyen bursiyerlerin bursları otomatik olarak kesilir. Kayıt yenilemek için lütfen her Eylül ayında formu doldurun.',
    },
    cta: {
      kicker: 'Yenileme',
      title: 'Bursunu yenile',
      text: 'Kayıt yenileme formunu doldurarak bursunuzun devamını sağlayabilirsiniz.',
      button: 'Kayıt Yenileme Sayfası',
      url: '/burs/yenile',
    },
    docs: {
      kicker: 'Gerekli Evraklar',
      title: 'Yenileme için hazırlayın',
      list: ['Güncel Öğrenci Belgesi', 'Güncel Transkript', 'Adres Beyanı', 'Banka IBAN'],
    },
  },
};

const TABS_ORDER = ['universite', 'ilk-orta-lise', 'yenileme'];

const keyFor = (tab, name) => `sch_${tab.replace(/-/g, '_')}_${name}`;

export default function Scholarships() {
  useSEO('scholarships');
  const [searchParams, setSearchParams] = useSearchParams();
  const settings = usePublicSettings();
  const [faqDb, setFaqDb] = useState(null);

  useEffect(() => {
    api.publicContent('faq').then(list => setFaqDb(Array.isArray(list) ? list : [])).catch(() => setFaqDb([]));
  }, []);

  const currentTab = TABS_ORDER.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'universite';
  const def = TAB_DEFS[currentTab];

  const switchTab = (t) => {
    setSearchParams({ tab: t });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Resolved apply URL for this tab (falls back to default)
  const applyUrl = settings[keyFor(currentTab, 'cta_url')] || def.cta.url;
  const buttonLabel = settings[keyFor(currentTab, 'cta_button')] || def.cta.button;
  const isExternal = /^https?:\/\//i.test(applyUrl);

  return (
    <EditRegion label={`Burslar — ${def.label}`} to="/admin/settings">
      <>
        <PageHero slug={`scholarships_${currentTab}`}
          kicker={def.hero.kicker}
          title={def.hero.title}
          text={def.hero.text}
        />

        {/* Tabs bar */}
        <div className="bg-cream border-b border-ink-100 sticky top-16 z-30" data-testid="scholarship-tabs">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex gap-1 overflow-x-auto no-scrollbar">
              {TABS_ORDER.map(t => {
                const Icon = TAB_DEFS[t].icon;
                const active = currentTab === t;
                return (
                  <button
                    key={t}
                    onClick={() => switchTab(t)}
                    data-testid={`tab-${t}`}
                    className={`inline-flex items-center gap-2 px-5 h-14 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${active ? 'border-ink-600 text-ink-600' : 'border-transparent text-ink-400 hover:text-ink-600'}`}
                  >
                    <Icon className="w-4 h-4" />
                    {TAB_DEFS[t].label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <section id="basvuru" className="bg-cream">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
            {/* Info cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-16">
              {def.cards.map((c, i) => (
                <div key={i} className="bg-white rounded-3xl border border-ink-100 p-8 hover:border-gold transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-2xl bg-ink-50 grid place-items-center text-ink-600">
                      <FileText className="w-5 h-5" />
                    </div>
                    <InlineText settingKey={keyFor(currentTab, `card${i}_tag`)} fallback={c.tag} as="span"
                      className={`text-[10px] uppercase tracking-widest font-semibold rounded-full px-2.5 py-1 ${c.tag ? 'bg-coral-100 text-coral-400' : ''}`} />
                  </div>
                  <InlineText settingKey={keyFor(currentTab, `card${i}_title`)} fallback={c.title} as="h3"
                    className="font-display text-2xl mt-5 text-ink-600" />
                  <InlineText settingKey={keyFor(currentTab, `card${i}_text`)} fallback={c.text} as="p"
                    multiline className="mt-2 text-ink-500 text-sm leading-relaxed" />
                </div>
              ))}
            </div>

            {/* Criteria + Apply CTA */}
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              {/* Criteria */}
              <div className="lg:col-span-5">
                <InlineText settingKey={keyFor(currentTab, 'crit_kicker')} fallback={def.criteria.kicker} as="div"
                  className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
                <InlineText settingKey={keyFor(currentTab, 'crit_title')} fallback={def.criteria.title} as="h2"
                  className="font-display text-4xl md:text-5xl mt-4 leading-[1.05] text-ink-600" />
                <EditableList
                  settingKey={keyFor(currentTab, 'criteria_list')}
                  defaultItems={def.criteria.list}
                  addLabel="Yeni koşul ekle"
                  listAs="ul"
                  className="mt-8 space-y-4"
                  itemClassName="pr-16"
                  renderItem={(text) => (
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-gold/20 text-gold-500 grid place-items-center shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-ink-600 flex-1 whitespace-pre-wrap">{text}</span>
                    </li>
                  )}
                />
                <div className="mt-8 p-5 rounded-2xl bg-coral-50 border border-coral-100 text-coral-400 text-sm">
                  <InlineText settingKey={keyFor(currentTab, 'crit_note')} fallback={def.criteria.note} as="span" multiline />
                </div>
              </div>

              {/* Application CTA (replaces the form) */}
              <div className="lg:col-span-7">
                <div className="bg-ink-600 text-cream rounded-3xl p-10 md:p-14 shadow-xl shadow-ink-900/10 relative overflow-hidden">
                  <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-16 -left-16 w-52 h-52 rounded-full bg-coral-300/10 blur-3xl pointer-events-none" />
                  <div className="relative">
                    <InlineText settingKey={keyFor(currentTab, 'cta_kicker')} fallback={def.cta.kicker} as="div"
                      className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold" />
                    <InlineText settingKey={keyFor(currentTab, 'cta_title')} fallback={def.cta.title} as="h3"
                      className="font-display text-3xl md:text-4xl mt-4 leading-[1.1]" />
                    <InlineText settingKey={keyFor(currentTab, 'cta_text')} fallback={def.cta.text} as="p"
                      multiline className="mt-4 text-cream/75 leading-relaxed max-w-md" />

                    {/* Apply button — always opens in a new tab, URL is hidden from readers */}
                    <div className="mt-8 flex items-center gap-3">
                      <a
                        href={applyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="apply-cta"
                        className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full pl-7 pr-6 h-14 text-base font-bold hover:bg-gold-400 transition-colors shadow-lg"
                      >
                        {buttonLabel} {isExternal ? <ExternalLink className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                      </a>

                      {/* Admin-only inline edit — button label & URL are hidden from public */}
                      <CtaEditButton
                        currentLabel={buttonLabel}
                        currentUrl={applyUrl}
                        labelKey={keyFor(currentTab, 'cta_button')}
                        urlKey={keyFor(currentTab, 'cta_url')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Documents — hidden on the 'yenileme' tab per user request */}
        {currentTab !== 'yenileme' && (
        <section id="evrak" className="bg-cream-200">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
            <div className="max-w-2xl">
              <InlineText settingKey={keyFor(currentTab, 'docs_kicker')} fallback={def.docs.kicker} as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
              <InlineText settingKey={keyFor(currentTab, 'docs_title')} fallback={def.docs.title} as="h2"
                className="font-display text-4xl md:text-5xl mt-4 leading-[1.05] text-ink-600" />
            </div>
            <EditableList
              settingKey={keyFor(currentTab, 'docs_list')}
              defaultItems={def.docs.list}
              addLabel="Yeni evrak ekle"
              listAs="div"
              className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4"
              renderItem={(text) => (
                <div className="bg-white rounded-2xl p-5 border border-ink-100 flex items-center gap-3 h-full">
                  <div className="w-10 h-10 rounded-xl bg-ink-50 grid place-items-center text-ink-600 shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="text-sm font-medium text-ink-600 flex-1 whitespace-pre-wrap">{text}</div>
                </div>
              )}
            />
          </div>
        </section>
        )}

        {/* FAQ (dynamic — admin-managed via /admin/faq, filtered by current tab) */}
        <section className="bg-cream">
          <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 md:py-28">
            <div className="text-center max-w-2xl mx-auto">
              <InlineText settingKey="sch_faq_kicker" fallback="Sıkça Sorulanlar" as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
              <InlineText settingKey="sch_faq_title" fallback="Merak edilenler" as="h2"
                className="font-display text-4xl md:text-5xl mt-4 leading-[1.05] text-ink-600" />
            </div>
            <FaqList db={faqDb} tab={currentTab} />

            {/* Admin add-CTA */}
            <div className="yekuv-admin-only mt-8 flex justify-center" style={{ display: 'none' }}>
              <Link
                to="/admin/faq"
                data-testid="add-faq"
                className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-6 h-12 text-sm font-semibold hover:bg-ink-500"
              >
                <Plus className="w-4 h-4" /> Yeni SSS Ekle · Düzenle
              </Link>
            </div>
          </div>
        </section>
      </>
    </EditRegion>
  );
}

/** Renders FAQ items filtered by current tab. Falls back to hardcoded mock when DB empty. */
function FaqList({ db, tab }) {
  // While loading: show hardcoded items
  const list = (() => {
    if (db === null) return FAQ.map((f, i) => ({ id: `mock-${i}`, q: f.q, a: f.a }));
    if (db.length === 0) return FAQ.map((f, i) => ({ id: `mock-${i}`, q: f.q, a: f.a }));
    return db
      .map(x => ({ id: x.id, ...(x.data || {}) }))
      .filter(x => !x.scope || x.scope === 'all' || x.scope === tab)
      .map(x => ({ id: x.id, q: x.question || '(soru boş)', a: x.answer || '' }));
  })();

  if (list.length === 0) {
    return (
      <div className="mt-12 text-center text-ink-400 border-t border-b border-ink-100 py-16">
        Bu sayfada gösterilecek SSS yok. Admin panelinden ekleyebilirsin.
      </div>
    );
  }

  return (
    <div className="mt-12 divide-y divide-ink-100 border-t border-b border-ink-100" data-testid="faq-list">
      {list.map((f) => (
        <EditRegion key={f.id} label="Bu SSS'yi Düzenle" to="/admin/faq">
          <FaqItem q={f.q} a={f.a} />
        </EditRegion>
      ))}
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="w-full text-left py-6 flex items-center justify-between gap-6">
        <span className="font-display text-xl md:text-2xl text-ink-600">{q}</span>
        <span className={`w-10 h-10 rounded-full grid place-items-center transition-transform ${open ? 'bg-ink-600 text-cream rotate-45' : 'bg-cream-200 text-ink-600'}`}>+</span>
      </button>
      {open && <div className="pb-6 text-ink-500 leading-relaxed max-w-3xl">{a}</div>}
    </div>
  );
}

/**
 * Admin-only pencil chip that shows next to the CTA button.
 * Opens a modal with 2 fields (label + URL) — much better UX than two
 * consecutive window.prompt() calls (which browsers can suppress).
 * Invisible to non-admin visitors, so the URL is never exposed publicly.
 */
function CtaEditButton({ currentLabel, currentUrl, labelKey, urlKey }) {
  const canEdit = useEditMode();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(currentLabel);
  const [url, setUrl] = useState(currentUrl);
  const [saving, setSaving] = useState(false);

  if (!canEdit) return null;

  const openModal = () => {
    setLabel(currentLabel);
    setUrl(currentUrl);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const patches = {};
      const cleanLabel = (label || '').trim();
      const cleanUrl = (url || '').trim();
      if (!cleanLabel) { toast.error('Buton yazısı boş olamaz'); setSaving(false); return; }
      if (!cleanUrl) { toast.error('Buton linki boş olamaz'); setSaving(false); return; }
      if (cleanLabel !== currentLabel) {
        await api.adminUpsertSetting(labelKey, cleanLabel);
        patches[labelKey] = cleanLabel;
      }
      if (cleanUrl !== currentUrl) {
        await api.adminUpsertSetting(urlKey, cleanUrl);
        patches[urlKey] = cleanUrl;
      }
      if (Object.keys(patches).length > 0) {
        patchPublicSettings(patches);
        toast.success('Buton güncellendi');
      }
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="cta-edit-btn"
        title="Buton yazısı & linkini düzenle"
        className="inline-flex items-center gap-1.5 h-10 px-3 rounded-full bg-cream/10 hover:bg-cream/20 border border-cream/20 text-cream/80 text-xs font-medium transition-colors"
      >
        <Pencil className="w-3.5 h-3.5" />
        Butonu Düzenle
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-ink-900/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="cta-edit-modal"
        >
          <div className="bg-white text-ink-600 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-8">
              <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Buton Ayarları</div>
              <h3 className="font-display text-3xl mt-2">Butonu Düzenle</h3>
              <p className="mt-2 text-ink-400 text-sm">Buton yazısı ve tıklandığında yönlendirileceği bağlantıyı buradan güncelleyin.</p>

              <div className="mt-6 space-y-4">
                <div>
                  <label className="text-[10px] tracking-widest uppercase text-ink-400 font-bold">Buton yazısı</label>
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    data-testid="cta-edit-label"
                    placeholder="Burs Başvuru Sayfası"
                    className="mt-2 w-full h-12 px-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 focus:ring-2 focus:ring-ink-500/10 outline-none text-ink-600"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-widest uppercase text-ink-400 font-bold">Buton linki (tıklayınca gidecek adres)</label>
                  <input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    data-testid="cta-edit-url"
                    placeholder="https://forms.google.com/... veya /burs/basvuru"
                    className="mt-2 w-full h-12 px-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 focus:ring-2 focus:ring-ink-500/10 outline-none text-ink-600 font-mono text-sm"
                  />
                  <div className="mt-2 text-xs text-ink-400 leading-relaxed">
                    • Dış link (Google Form, başka site vb.) için tam URL yaz: <span className="font-mono text-ink-500">https://...</span><br />
                    • Site içi sayfa için: <span className="font-mono text-ink-500">/burs/basvuru</span> gibi yaz.<br />
                    • Buton her zaman yeni sekmede açılır.
                  </div>
                </div>
              </div>
            </div>

            <div className="px-8 py-5 bg-cream-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="cta-edit-cancel"
                className="h-11 px-5 rounded-full text-ink-500 hover:text-ink-600 text-sm font-semibold"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="cta-edit-save"
                className="h-11 px-6 rounded-full bg-ink-600 text-cream text-sm font-bold hover:bg-ink-500 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {saving ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
